/**
 * Investor Purchase Advice Tab
 * Tab 3 of Banker Recon - handles investor info, pricing, adjustments, and document upload
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Upload,
  FileText,
  TrendingUp,
  TrendingDown,
  Building2,
  Eye,
  Trash2,
  Loader2,
  Save,
  Check,
  Plus,
  X,
  Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/utils/supabase';
import { uploadPurchaseAdvice } from '@/services/purchaseAdviceUploadService';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { resolveActiveLockInvestor } from '@/utils/investorResolution';
import type { EncompassLoan } from '@/components/broker-recon/LoanAutocomplete';
import {
  type PricingState,
  type CostsState,
  type RevenueState,
  type UploadedDocument,
  type PurchaseAdjustment,
  computeInvestorLockPrice,
  AUTO_EXTRACTED_PRICE_ID,
  INVESTOR_FEE_OPTIONS,
  formatCurrency as formatCurrencyFn,
  formatNumberWithCommas as formatNumberWithCommasFn,
  parseFormattedNumber as parseFormattedNumberFn
} from './types';
import { recomputeAutoPriceBasis } from './autoFillFromDocuments';
import {
  BUYDOWN_AUTO_LINE_TYPE,
  BUYDOWN_DISPLAY_DESCRIPTION,
  canonicalInvestorCostBaseType
} from '@/utils/banker-recon/buydownPassThrough';

// Re-exported for pdfGenerator.ts / BankerReconSummary.tsx which import it from here.
export type { PurchaseAdjustment } from './types';

// INVESTOR_FEE_OPTIONS now lives in ./types (shared with the auto-fill mapper/modal + feedback table).

// ============================================================================
// PURCHASE PRICE ADJUSTMENT OPTIONS
// ============================================================================

const PURCHASE_ADJUSTMENTS = [
  { id: 'mandatory_trade', description: 'Mandatory Trade', allowMultiple: false },
  { id: 'extensions', description: 'Extensions', allowMultiple: true },
  { id: 'suspense_fee', description: 'Suspense Fee', allowMultiple: true },
  { id: 'rounding', description: 'Rounding', allowMultiple: false }
] as const;

// ============================================================================
// PROPS
// ============================================================================

interface InvestorAdviceTabProps {
  selectedLoan: EncompassLoan | null;
  user: { id: string; organization_id: string } | null;

  // Pricing
  pricing: PricingState;
  setPricing: React.Dispatch<React.SetStateAction<PricingState>>;

  // Costs (investor costs)
  costs: CostsState;
  setCosts: React.Dispatch<React.SetStateAction<CostsState>>;

  // Revenue (investor revenue including SRP)
  revenue: RevenueState;
  setRevenue: React.Dispatch<React.SetStateAction<RevenueState>>;

  // Documents
  uploadedDocuments: UploadedDocument[];
  setUploadedDocuments: React.Dispatch<React.SetStateAction<UploadedDocument[]>>;

  // Calculated values
  totalInvestorCosts: number;
  totalInvestorRevenue: number;

  // Dirty state callback
  setIsDirty: (dirty: boolean) => void;

  // Save functionality
  onSaveDraft: () => Promise<unknown>;
  isSaving: boolean;
  isDirty: boolean;
  saveSuccess: boolean;

  // Investor purchase date
  investorPurchaseDate: string;
  setInvestorPurchaseDate: (date: string) => void;

  // First payment to investor
  firstPaymentToInvestor: string;
  setFirstPaymentToInvestor: (date: string) => void;

  // Purchase price adjustments
  purchaseAdjustments: PurchaseAdjustment[];
  setPurchaseAdjustments: React.Dispatch<React.SetStateAction<PurchaseAdjustment[]>>;

  // Auto-fill: notify the parent coordinator when a doc finishes extracting
  onDocumentReady?: (documentId: string) => void;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({ value, onChange, className, placeholder }) => {
  const [displayValue, setDisplayValue] = useState(value ? formatNumberWithCommasFn(value) : '');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setDisplayValue(value ? formatNumberWithCommasFn(value) : '');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitized = rawValue.replace(/[^0-9.,-]/g, '');
    setDisplayValue(sanitized);
    onChange(parseFormattedNumberFn(sanitized));
  };

  const handleBlur = () => {
    if (displayValue) {
      const num = parseFormattedNumberFn(displayValue);
      setDisplayValue(num ? formatNumberWithCommasFn(num) : '');
    }
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder}
    />
  );
};

// ============================================================================
// NUMERIC INPUT (decimal, allows negatives, preserves intermediate states)
// ============================================================================

interface NumericInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
}

const numberToDisplay = (n: number): string =>
  Number.isFinite(n) && n !== 0 ? String(n) : '';

const isPartialNumeric = (s: string): boolean =>
  s === '-' || s === '.' || s === '-.';

export const NumericInput: React.FC<NumericInputProps> = ({ value, onChange, className, placeholder }) => {
  const [displayValue, setDisplayValue] = useState(numberToDisplay(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setDisplayValue(numberToDisplay(value));
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) {
      return;
    }
    setDisplayValue(raw);

    if (raw === '') {
      onChange(0);
      return;
    }
    if (isPartialNumeric(raw)) {
      return;
    }
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed)) {
      onChange(parsed);
    }
  };

  const handleBlur = () => {
    setDisplayValue(numberToDisplay(value));
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder}
    />
  );
};

// ============================================================================
// COMPONENT
// ============================================================================

export const InvestorAdviceTab: React.FC<InvestorAdviceTabProps> = ({
  selectedLoan,
  user,
  pricing,
  setPricing,
  costs,
  setCosts,
  uploadedDocuments,
  setUploadedDocuments,
  totalInvestorCosts,
  setIsDirty,
  onSaveDraft,
  isSaving,
  isDirty,
  saveSuccess,
  investorPurchaseDate,
  setInvestorPurchaseDate,
  firstPaymentToInvestor,
  setFirstPaymentToInvestor,
  purchaseAdjustments,
  setPurchaseAdjustments,
  onDocumentReady
}) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [investorName, setInvestorName] = useState('');
  const [selectedInvestorFee, setSelectedInvestorFee] = useState('');
  const [selectedPurchaseAdjustment, setSelectedPurchaseAdjustment] = useState('');
  const [roundingInputMode, setRoundingInputMode] = useState<'bps' | 'dollar'>('dollar');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-populate investor name. Parent (BankerRecon) overlays the active
  // delivery lock onto selectedLoan via applyDeliveryLockToLoan, so
  // selectedLoan.rate_lock_loan_program and selectedLoan.investor_name
  // already carry the active-lock values. Program-first resolution: the
  // captured program is authoritative; investor_name may be stale.
  useEffect(() => {
    if (selectedLoan) {
      const resolved = resolveActiveLockInvestor(
        selectedLoan.rate_lock_loan_program,
        selectedLoan.investor_name
      );
      setInvestorName(resolved ?? '');
    } else {
      // Clear when loan is cleared (investorPurchaseDate is cleared by parent)
      setInvestorName('');
      setSelectedInvestorFee('');
    }
  }, [selectedLoan]);

  // Filter documents to only purchase_advice type
  const purchaseAdviceDocuments = uploadedDocuments.filter(
    doc => doc.documentType === 'purchase_advice'
  );

  // Calculate investor lock price (shared helper — kept identical to the parent coordinator)
  const investorLockPrice = React.useMemo(
    () => computeInvestorLockPrice(selectedLoan),
    [selectedLoan]
  );

  // Calculate pricing breakdown for Investor Pricing section
  const pricingBreakdown = React.useMemo(() => {
    const adjustments = selectedLoan?.rate_lock_buy_side_adjustments || [];
    const loanAmount = selectedLoan?.trans_details_loan_amt || 0;

    // LLPA adjustments (BasePrice type)
    const llpaAdjustments = adjustments.filter(adj => adj.priceAdjustmentType === 'BasePrice');
    const llpaTotal = llpaAdjustments.reduce((sum, adj) => sum + adj.rate, 0);

    // Margin adjustments (ProfitMargin type)
    const marginAdjustments = adjustments.filter(adj => adj.priceAdjustmentType === 'ProfitMargin');
    const marginTotal = marginAdjustments.reduce((sum, adj) => sum + Math.abs(adj.rate), 0);

    // Base price = total buy price - LLPA (Encompass buy price already includes LLPA)
    const encompassBuyPrice = selectedLoan?.rate_lock_total_buy_price || 0;
    const basePrice = encompassBuyPrice - llpaTotal;

    // Calculate dollar amounts (points above par)
    const baseDollar = loanAmount * (basePrice - 100) / 100;
    const llpaDollar = loanAmount * llpaTotal / 100;
    const marginDollar = loanAmount * marginTotal / 100;
    const totalDollar = loanAmount * (investorLockPrice - 100) / 100;

    return {
      basePrice,
      baseDollar,
      llpaTotal,
      llpaDollar,
      marginTotal,
      marginDollar,
      totalDollar
    };
  }, [selectedLoan, investorLockPrice]);

  // Calculate Actual Purchase Price (derived from Investor Lock Price + Adjustments)
  const actualPurchasePrice = React.useMemo(() => {
    const adjustmentTotal = purchaseAdjustments.reduce((sum, adj) => sum + adj.basisPoints, 0);
    return investorLockPrice + adjustmentTotal;
  }, [investorLockPrice, purchaseAdjustments]);

  // Calculate Actual Purchase Price in dollars
  const actualPurchaseDollar = React.useMemo(() => {
    const loanAmount = selectedLoan?.trans_details_loan_amt || 0;
    return loanAmount * (actualPurchasePrice - 100) / 100;
  }, [actualPurchasePrice, selectedLoan?.trans_details_loan_amt]);

  // Sync actualPurchasePrice to pricing state (for variance calculation and DB save)
  useEffect(() => {
    if (actualPurchasePrice !== pricing.actualPricePercent) {
      setPricing(prev => ({ ...prev, actualPricePercent: actualPurchasePrice }));
    }
  }, [actualPurchasePrice, pricing.actualPricePercent, setPricing]);

  // Keep the synthetic auto_extracted_price adjustment pinned to the document's target price as the
  // investor lock price moves, so the derived Actual Purchase Price stays equal to the document value.
  // recomputeAutoPriceBasis returns the same array reference when nothing changes (no render loop).
  useEffect(() => {
    setPurchaseAdjustments(prev => recomputeAutoPriceBasis(prev, investorLockPrice));
  }, [investorLockPrice, setPurchaseAdjustments]);

  // Get available purchase adjustment options
  const availablePurchaseAdjustments = React.useMemo(() => {
    // Check which adjustments are already added
    const existingAdjustmentTypes = new Set(
      purchaseAdjustments.map(adj => adj.id.replace(/_\d+$/, ''))
    );

    return PURCHASE_ADJUSTMENTS.filter(option => {
      // If allowMultiple, always show it
      if (option.allowMultiple) {
return true;
}
      // Otherwise, only show if not already added
      return !existingAdjustmentTypes.has(option.id);
    });
  }, [purchaseAdjustments]);

  // Calculate pricing variance
  const actualPrice = pricing.actualPricePercent;
  const variance = actualPrice - investorLockPrice;
  const varianceIsPositive = variance >= 0;

  // Get available investor fee options (exclude already-added fees)
  const availableInvestorFees = React.useMemo(() => {
    // Get base IDs of already-added fees (strip timestamp suffix)
    const existingFeeIds = new Set(
      costs.investor.map(item => canonicalInvestorCostBaseType(item.id, item.description))
    );
    return INVESTOR_FEE_OPTIONS.filter(option => !existingFeeIds.has(option.id));
  }, [costs.investor]);

  // ============================================================================
  // COST/REVENUE UPDATE HANDLERS
  // ============================================================================

  const updateInvestorCost = useCallback((id: string, amount: number) => {
    setCosts(prev => ({
      ...prev,
      investor: prev.investor.map(item =>
        item.id === id ? { ...item, amount } : item
      )
    }));
    setIsDirty(true);
  }, [setCosts, setIsDirty]);

  const addInvestorCost = useCallback(() => {
    if (!selectedInvestorFee) {
return;
}

    const fee = INVESTOR_FEE_OPTIONS.find(f => f.id === selectedInvestorFee);
    if (!fee) {
return;
}

    const id = fee.id === 'interest_rate_buydown'
      ? BUYDOWN_AUTO_LINE_TYPE
      : `${fee.id}_${Date.now()}`;
    const description = fee.id === 'interest_rate_buydown'
      ? BUYDOWN_DISPLAY_DESCRIPTION
      : fee.description;

    setCosts(prev => ({
      ...prev,
      investor: [
        ...prev.investor.filter(item => canonicalInvestorCostBaseType(item.id, item.description) !== fee.id),
        { id, description, amount: 0 }
      ]
    }));
    setSelectedInvestorFee('');
    setIsDirty(true);
  }, [selectedInvestorFee, setCosts, setIsDirty]);

  const removeInvestorCost = useCallback((id: string) => {
    setCosts(prev => ({
      ...prev,
      investor: prev.investor.filter(item => item.id !== id)
    }));
    setIsDirty(true);
  }, [setCosts, setIsDirty]);

  // ============================================================================
  // PURCHASE ADJUSTMENT HANDLERS
  // ============================================================================

  const addPurchaseAdjustment = useCallback(() => {
    if (!selectedPurchaseAdjustment) {
return;
}

    const adjustmentType = PURCHASE_ADJUSTMENTS.find(a => a.id === selectedPurchaseAdjustment);
    if (!adjustmentType) {
return;
}

    // Create unique ID (with timestamp for items that allow multiples)
    const id = adjustmentType.allowMultiple
      ? `${adjustmentType.id}_${Date.now()}`
      : adjustmentType.id;

    setPurchaseAdjustments(prev => [
      ...prev,
      {
        id,
        description: adjustmentType.description,
        basisPoints: 0
      }
    ]);
    setSelectedPurchaseAdjustment('');
    setIsDirty(true);
  }, [selectedPurchaseAdjustment, setPurchaseAdjustments, setIsDirty]);

  const updatePurchaseAdjustment = useCallback((id: string, basisPoints: number) => {
    setPurchaseAdjustments(prev =>
      prev.map(adj => adj.id === id ? { ...adj, basisPoints } : adj)
    );
    setIsDirty(true);
  }, [setPurchaseAdjustments, setIsDirty]);

  const removePurchaseAdjustment = useCallback((id: string) => {
    setPurchaseAdjustments(prev => prev.filter(adj => adj.id !== id));
    setIsDirty(true);
  }, [setPurchaseAdjustments, setIsDirty]);

  // ============================================================================
  // DOCUMENT UPLOAD HANDLERS
  // ============================================================================

  const processFileUpload = useCallback(async (file: File) => {
    if (!user || !selectedLoan) {
      return;
    }

    setUploading(true);

    try {
      // Use shared PA upload service for storage + DB + queue
      const result = await uploadPurchaseAdvice({
        file,
        organizationId: user.organization_id,
        encompassLoanId: selectedLoan.encompass_loan_id,
        userId: user.id,
        uploadContext: 'banker_recon'
      });

      // Add to uploaded documents with purchase_advice type
      const newDoc: UploadedDocument = {
        id: result.docId,
        file,
        fileName: file.name,
        documentType: 'purchase_advice',
        confidence: 1,
        storagePath: result.storagePath,
        uploadedAt: new Date(),
        processingStatus: 'queued',
        progress: 0
      };

      setUploadedDocuments(prev => [...prev, newDoc]);
      setIsDirty(true);

      // Subscribe to queue updates for extraction progress
      const channel = supabase
        .channel(`investor-doc-${result.docId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'document_processing_queue',
            filter: `storage_path=eq.${result.storagePath}`
          },
          (payload) => {
            const queueItem = payload.new as {
              status: string;
              extracted_text: string | null;
              error_message: string | null;
              progress: number;
            };

            if (queueItem.status === 'extracted' || queueItem.status === 'completed') {
              setUploadedDocuments(prev =>
                prev.map(doc =>
                  doc.id === result.docId
                    ? { ...doc, processingStatus: 'complete' as const }
                    : doc
                )
              );
              toast({
                title: 'Document processed',
                description: 'Document text extracted and attached to record'
              });
              onDocumentReady?.(result.docId); // hand off to the parent auto-fill coordinator
              channel.unsubscribe();
            } else if (queueItem.status === 'failed') {
              setUploadedDocuments(prev =>
                prev.map(doc =>
                  doc.id === result.docId
                    ? { ...doc, processingStatus: 'failed' as const }
                    : doc
                )
              );
              toast({
                variant: 'destructive',
                title: 'Processing failed',
                description: queueItem.error_message || 'Document extraction failed'
              });
              channel.unsubscribe();
            } else {
              setUploadedDocuments(prev =>
                prev.map(doc =>
                  doc.id === result.docId
                    ? { ...doc, progress: Math.round(queueItem.progress || 0), processingStatus: 'extracting' as const }
                    : doc
                )
              );
            }
          }
        )
        .subscribe();

      toast({
        title: 'Document uploaded',
        description: 'Processing purchase advice document...'
      });

    } catch (err) {
      console.error('Upload failed:', err);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: 'Could not upload document'
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [user, selectedLoan, setUploadedDocuments, setIsDirty, toast, onDocumentReady]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (selectedLoan) {
      setIsDragging(true);
    }
  }, [selectedLoan]);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (!selectedLoan) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type === 'application/pdf') {
      processFileUpload(files[0]);
    } else {
      toast({
        variant: 'destructive',
        title: 'Invalid file',
        description: 'Please upload a PDF file'
      });
    }
  }, [selectedLoan, toast, processFileUpload]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileUpload(file);
    }
  }, [processFileUpload]);

  const handleViewDocument = useCallback(async (storagePath: string) => {
    const { data } = await supabase.storage
      .from('loan-documents')
      .createSignedUrl(storagePath, 3600);

    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    } else {
      toast({
        variant: 'destructive',
        title: 'Could not open document',
        description: 'Failed to generate signed URL'
      });
    }
  }, [toast]);

  const handleRemoveDocument = useCallback(async (documentId: string) => {
    const doc = uploadedDocuments.find(d => d.id === documentId);

    // Delete from storage
    if (doc?.storagePath) {
      await supabase.storage
        .from('loan-documents')
        .remove([doc.storagePath]);
    }

    // Delete from loan_documents table
    await supabase
      .from('loan_documents')
      .delete()
      .eq('id', documentId);

    setUploadedDocuments(prev => prev.filter(d => d.id !== documentId));
    setIsDirty(true);
    toast({ title: 'Document removed' });
  }, [uploadedDocuments, setUploadedDocuments, setIsDirty, toast]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Save Draft button component for reuse
  const SaveDraftButton = () => (
    <Button
      onClick={onSaveDraft}
      disabled={isSaving || saveSuccess || !selectedLoan || !isDirty}
      variant={saveSuccess ? "outline" : isDirty ? "default" : "outline"}
      size="sm"
      className={saveSuccess ? "text-green-600 border-green-600" : ""}
    >
      {isSaving ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : saveSuccess ? (
        <Check className="h-4 w-4 mr-2" />
      ) : (
        <Save className="h-4 w-4 mr-2" />
      )}
      {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Draft'}
    </Button>
  );

  return (
    <div className="space-y-6">
      {/* Top Save Button */}
      <div className="flex justify-end">
        <SaveDraftButton />
      </div>

      {/* Document Upload Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-gray-500" />
            Purchase Advice Document
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Upload Zone */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
                isDragging
                  ? "border-blue-500 bg-blue-50"
                  : !selectedLoan
                    ? "border-gray-200 opacity-50"
                    : "border-gray-300 hover:border-blue-400"
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf"
                onChange={handleFileChange}
                disabled={uploading || !selectedLoan}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !selectedLoan}
                className={cn(
                  "w-full flex flex-col items-center gap-2",
                  (!selectedLoan || uploading) && "opacity-50 cursor-not-allowed"
                )}
              >
                <Upload className="h-8 w-8 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                </span>
                <span className="text-xs text-gray-400">PDF files only</span>
              </button>
            </div>

            {/* Processing Progress */}
            {purchaseAdviceDocuments.some(d => d.processingStatus && d.processingStatus !== 'complete' && d.processingStatus !== 'failed') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <span className="text-sm text-blue-700">
                    Extracting text from document...
                  </span>
                </div>
                <Progress
                  value={100}
                  className="h-2 mt-2 [&>div]:animate-pulse"
                />
              </div>
            )}

            {/* Uploaded Documents */}
            {purchaseAdviceDocuments.length > 0 && (
              <div className="space-y-2">
                {purchaseAdviceDocuments.map(doc => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">{doc.fileName || doc.file?.name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {doc.processingStatus === 'complete' ? (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              Processed
                            </Badge>
                          ) : doc.processingStatus === 'failed' ? (
                            <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                              Failed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              Processing...
                            </Badge>
                          )}
                          <span className="text-gray-400">•</span>
                          <span>{doc.uploadedAt.toLocaleDateString()} {doc.uploadedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.processingStatus === 'complete' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewDocument(doc.storagePath)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveDocument(doc.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Investor Info Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5 text-gray-500" />
            Investor Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="investorName" className="text-xs text-gray-500">Investor Name</Label>
              <Input
                id="investorName"
                value={investorName}
                onChange={(e) => {
                  setInvestorName(e.target.value);
                  setIsDirty(true);
                }}
                placeholder="e.g., Plaza Home Mortgage"
              />
            </div>
            <div>
              <Label htmlFor="purchaseDate" className="text-xs text-gray-500">Purchase Date</Label>
              <Input
                id="purchaseDate"
                type="date"
                value={investorPurchaseDate}
                onChange={(e) => {
                  setInvestorPurchaseDate(e.target.value);
                  setIsDirty(true);
                }}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Loan Amount</Label>
              <p className="font-medium text-lg mt-1.5">
                {selectedLoan?.trans_details_loan_amt
                  ? formatCurrencyFn(selectedLoan.trans_details_loan_amt)
                  : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* First Payment Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-blue-500" />
            First Payment Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {/* First Payment on Loan - Read-only from Encompass */}
            <div>
              <Label className="text-xs text-gray-500">First Payment on Loan</Label>
              <p className="font-medium text-lg mt-1.5">
                {selectedLoan?.first_payment_date
                  ? parseAsLocalDate(selectedLoan.first_payment_date).toLocaleDateString()
                  : '—'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">From Encompass</p>
            </div>
            {/* First Payment to Investor - Editable */}
            <div>
              <Label htmlFor="firstPaymentToInvestor" className="text-xs text-gray-500">
                First Payment to Investor
              </Label>
              <Input
                id="firstPaymentToInvestor"
                type="date"
                value={firstPaymentToInvestor}
                onChange={(e) => {
                  setFirstPaymentToInvestor(e.target.value);
                  setIsDirty(true);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-gray-500" />
            Pricing Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-4">
            {/* Investor Lock Price (read-only from Encompass) */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Price at Lock</p>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold font-mono">
                  {investorLockPrice ? investorLockPrice.toFixed(4) : '—'}
                </span>
                <span className="text-slate-500">%</span>
              </div>
            </div>

            {/* Actual Price (derived from adjustments) */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Actual Purchase Price</p>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold font-mono text-blue-700">
                  {actualPurchasePrice ? actualPurchasePrice.toFixed(4) : '—'}
                </span>
                <span className="text-blue-500">%</span>
              </div>
              <p className="text-xs text-blue-500 mt-1">Calculated from adjustments below</p>
            </div>

            {/* Variance */}
            <div className={cn(
              "rounded-lg p-4 border",
              varianceIsPositive
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200"
            )}>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Variance</p>
              <p className={cn(
                "text-2xl font-bold font-mono",
                varianceIsPositive ? "text-green-700" : "text-red-700"
              )}>
                {varianceIsPositive ? '+' : ''}{variance.toFixed(3)}%
              </p>
              <p className={cn(
                "text-xs mt-1",
                varianceIsPositive ? "text-green-600" : "text-red-600"
              )}>
                {varianceIsPositive ? 'Above expectations' : 'Below expectations'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Investor Deductions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingDown className="h-5 w-5 text-red-500" />
            Investor Deductions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded-lg p-4 space-y-1">
            {costs.investor.map(item => (
              <div key={item.id} className="flex items-center gap-2 py-1.5">
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-gray-400">├─</span>
                  <span className="text-sm text-gray-600">{item.description}</span>
                </div>
                <CurrencyInput
                  value={item.amount}
                  onChange={(val) => updateInvestorCost(item.id, val)}
                  className="w-32 text-right"
                  placeholder="0.00"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                  onClick={() => removeInvestorCost(item.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}

            {/* Add Fee Dropdown - only show when there are fees to add */}
            {availableInvestorFees.length > 0 && (
              <div className="flex items-center gap-2 pt-3">
                <div className="flex-1 flex items-center gap-2">
                  <Select
                    value={selectedInvestorFee}
                    onValueChange={setSelectedInvestorFee}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Add investor fee..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableInvestorFees.map(option => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addInvestorCost}
                    disabled={!selectedInvestorFee}
                    className="gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </Button>
                </div>
                <div className="w-6" />
              </div>
            )}

            <div className="flex items-center justify-between py-2 mt-3 font-semibold border-t">
              <span className="text-gray-700">Total Investor Deductions</span>
              <span className="font-mono text-red-600">
                {formatCurrencyFn(-totalInvestorCosts)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Investor Pricing Breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Investor Pricing Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-0">
            {/* Header */}
            <div className="grid grid-cols-3 gap-4 text-xs font-medium text-gray-500 uppercase pb-2 border-b">
              <span>Item</span>
              <span className="text-right">Basis Points</span>
              <span className="text-right">Amount</span>
            </div>

            {/* Base Price */}
            <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span className="text-gray-600">Base Price</span>
              <span className="text-right font-mono">{pricingBreakdown.basePrice.toFixed(4)}</span>
              <span className="text-right font-mono text-green-600">{formatCurrencyFn(pricingBreakdown.baseDollar)}</span>
            </div>

            {/* LLPA Adjustments */}
            <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span className="text-gray-600">LLPA Adjustments</span>
              <span className="text-right font-mono text-green-600">+{pricingBreakdown.llpaTotal.toFixed(4)}</span>
              <span className="text-right font-mono text-green-600">{formatCurrencyFn(pricingBreakdown.llpaDollar)}</span>
            </div>

            {/* Lendwise Margin */}
            <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
              <span className="text-gray-600">Lendwise Margin</span>
              <span className="text-right font-mono text-green-600">+{pricingBreakdown.marginTotal.toFixed(4)}</span>
              <span className="text-right font-mono text-green-600">{formatCurrencyFn(pricingBreakdown.marginDollar)}</span>
            </div>

            {/* Total - Investor Lock Price */}
            <div className="grid grid-cols-3 gap-4 py-3 bg-gray-50 rounded-lg mt-2 px-2 font-semibold">
              <span className="text-gray-700">Investor Lock Price</span>
              <span className="text-right font-mono">{investorLockPrice.toFixed(4)}</span>
              <span className="text-right font-mono text-green-600">{formatCurrencyFn(pricingBreakdown.totalDollar)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Price Adjustments */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingDown className="h-5 w-5 text-orange-500" />
            Purchase Price Adjustments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-0">
            {/* Header */}
            <div className="grid grid-cols-3 gap-4 text-xs font-medium text-gray-500 uppercase pb-2 border-b">
              <span>Adjustment</span>
              <span className="text-right">Basis Points</span>
              <span className="text-right">Amount</span>
            </div>

            {/* Adjustment rows */}
            {purchaseAdjustments.map(adj => {
              const loanAmount = selectedLoan?.trans_details_loan_amt || 0;
              const adjDollar = loanAmount * adj.basisPoints / 100;
              const isRounding = adj.id === 'rounding';
              const isAutoExtracted = adj.id === AUTO_EXTRACTED_PRICE_ID;

              return (
                <div key={adj.id} className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100 items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">{adj.description}</span>
                    {isAutoExtracted && (
                      <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                        from document
                      </Badge>
                    )}
                    {isRounding && (
                      <div className="inline-flex rounded-md border border-gray-200 text-[10px] overflow-hidden">
                        <button
                          type="button"
                          className={cn(
                            "px-1.5 py-0.5 transition-colors",
                            roundingInputMode === 'dollar'
                              ? "bg-blue-100 text-blue-700 font-semibold"
                              : "text-gray-500 hover:bg-gray-50"
                          )}
                          onClick={() => setRoundingInputMode('dollar')}
                        >
                          $
                        </button>
                        <button
                          type="button"
                          className={cn(
                            "px-1.5 py-0.5 transition-colors border-l border-gray-200",
                            roundingInputMode === 'bps'
                              ? "bg-blue-100 text-blue-700 font-semibold"
                              : "text-gray-500 hover:bg-gray-50"
                          )}
                          onClick={() => setRoundingInputMode('bps')}
                        >
                          bps
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end">
                    {isAutoExtracted ? (
                      // Read-only: the pin effect keeps this in sync with the document target as the lock moves.
                      <span className="w-28 text-right font-mono text-gray-700">{adj.basisPoints.toFixed(4)}</span>
                    ) : isRounding && roundingInputMode === 'dollar' ? (
                      <NumericInput
                        value={loanAmount > 0 ? parseFloat(adjDollar.toFixed(2)) : 0}
                        onChange={(dollarVal) => {
                          const bps = loanAmount > 0 ? (dollarVal / loanAmount) * 100 : 0;
                          updatePurchaseAdjustment(adj.id, parseFloat(bps.toFixed(6)));
                        }}
                        className="w-28 text-right font-mono"
                        placeholder="-12.50"
                      />
                    ) : (
                      <NumericInput
                        value={adj.basisPoints}
                        onChange={(v) => updatePurchaseAdjustment(adj.id, v)}
                        className="w-28 text-right font-mono"
                        placeholder="-0.250"
                      />
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <span className={cn(
                      "font-mono",
                      adj.basisPoints < 0 ? "text-red-600" : adj.basisPoints > 0 ? "text-green-600" : "text-gray-500"
                    )}>
                      {isRounding && roundingInputMode === 'dollar'
                        ? `${adj.basisPoints >= 0 ? '' : '-'}${Math.abs(adj.basisPoints).toFixed(4)} bps`
                        : formatCurrencyFn(adjDollar)
                      }
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                      onClick={() => removePurchaseAdjustment(adj.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}

            {/* Add adjustment dropdown */}
            {availablePurchaseAdjustments.length > 0 && (
              <div className="flex items-center gap-2 py-3 border-b border-gray-100">
                <Select
                  value={selectedPurchaseAdjustment}
                  onValueChange={setSelectedPurchaseAdjustment}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Add adjustment..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePurchaseAdjustments.map(option => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addPurchaseAdjustment}
                  disabled={!selectedPurchaseAdjustment}
                  className="gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </div>
            )}

            {/* Actual Purchase Price total - always visible */}
            <div className="grid grid-cols-3 gap-4 py-3 bg-blue-50 rounded-lg mt-3 px-2 font-semibold">
              <span className="text-blue-700">Actual Purchase Price</span>
              <span className="text-right font-mono text-blue-700">{actualPurchasePrice.toFixed(4)}</span>
              <span className="text-right font-mono text-blue-600">{formatCurrencyFn(actualPurchaseDollar)}</span>
            </div>

            {purchaseAdjustments.length === 0 && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                No adjustments added. Actual Purchase Price equals Investor Lock Price.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save Button */}
      <div className="flex justify-end">
        <SaveDraftButton />
      </div>
    </div>
  );
};
